import { DecimalPipe, getCurrencySymbol, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatPrefix, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDivider } from '@angular/material/list';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import Utils from 'src/app/utils';

import {
  FEED_PROFIT_CONSTANTS,
  FEED_PROFIT_ENERGY_VALUES,
} from '../../../../../assets/data/feedProfitConstants';
import { Calculation, GenderOptions } from '../../../models/feed-profit.model';

export enum EnergyTypes {
  startLessThan25 = 'startLessThan25',
  startMoreThan25 = 'startMoreThan25',
  endBoar = 'endBoar',
  gift = 'gift',
  feedGift = 'feedGift',
}

@Component({
  selector: 'app-baseline-settings',
  templateUrl: './baseline-settings.component.html',
  styleUrls: ['./baseline-settings.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatPrefix,
    MatLabel,
    MatInput,
    AgrNumberDirective,
    MatDivider,
    NgIf,
    TranslatePipe,
  ],
})
export class BaselineSettingsComponent implements OnInit {
  @Input() isExtendedSimulation: boolean;
  @Input() calculation: Calculation;

  currencySymbol: string;
  constants = FEED_PROFIT_CONSTANTS;
  energyValues = FEED_PROFIT_ENERGY_VALUES;
  public get energyTypes(): typeof EnergyTypes {
    return EnergyTypes;
  }

  form = new FormGroup({
    calculatedPriceLargerThan25Kg: new FormControl<number>(null, []),
    calculatedPriceLessThan25Kg: new FormControl<number>(null, []),
    averageWeightPerDeadGrowFinisher: new FormControl<number>(null, []),
    growthStartProcedure: new FormControl<number>(null, []),
    growthEndProcedure: new FormControl<number>(null, []),
  });

  initialLoad = true;

  constructor(
    private locale: LocaleService,
    private readonly decimalPipe: DecimalPipe,
  ) {}

  ngOnInit(): void {
    this.currencySymbol = getCurrencySymbol(this.locale.getCustomerCurrency(), 'narrow');
    this.setBaseLineSettingsValues(this.calculation);
    this.initialLoad = false;
  }

  setBaseLineSettingsValues(calculation: Calculation) {
    const baseline = this.isExtendedSimulation
      ? calculation?.extendedSimulation?.baseLine
      : calculation?.baseLine;

    this.controlledFormSetNumber(
      this.form.controls.calculatedPriceLargerThan25Kg,
      baseline?.calculatedPriceLargerThan25Kg,
    );
    this.controlledFormSetNumber(
      this.form.controls.calculatedPriceLessThan25Kg,
      baseline?.calculatedPriceLessThan25Kg,
    );
    this.controlledFormSetNumber(
      this.form.controls.averageWeightPerDeadGrowFinisher,
      baseline?.averageWeightPerDeadGrowFinisher,
    );
    this.controlledFormSetNumber(
      this.form.controls.growthStartProcedure,
      baseline?.growthStartProcedure,
    );
    this.controlledFormSetNumber(
      this.form.controls.growthEndProcedure,
      baseline?.growthEndProcedure,
    );
  }

  controlledFormSetNumber(formControl: FormControl<number>, value: number) {
    if (Utils.shouldUpdateNumericForm(formControl.value, value)) {
      formControl.setValue(value, { emitEvent: !this.initialLoad });
    }
  }

  calculateGrowthDifference(isStart: boolean, value: number, calculation: Calculation): number {
    if (isStart) {
      return value - this.constants.baselineSettings.growthStartNorm;
    }
    return (
      value -
      (calculation?.genders === GenderOptions.BOAR_GILT
        ? this.constants.baselineSettings.growthEndNorm_boar_gilt
        : this.constants.baselineSettings.growthEndNorm_barrow_gilt)
    );
  }

  roundEnergyValue(value: number, digitsInfo = '1.0-2') {
    return this.decimalPipe.transform(value, digitsInfo, this.locale.getLocale());
  }

  getCalculation() {
    const {
      calculatedPriceLargerThan25Kg,
      calculatedPriceLessThan25Kg,
      averageWeightPerDeadGrowFinisher,
      growthStartProcedure,
      growthEndProcedure,
    } = this.form.value;

    const newBaseLine = {
      calculatedPriceLargerThan25Kg,
      calculatedPriceLessThan25Kg,
      averageWeightPerDeadGrowFinisher,
      growthStartProcedure,
      growthEndProcedure,
    };
    const newCalculation: Calculation = this.isExtendedSimulation
      ? {
          ...this.calculation,
          extendedSimulation: {
            ...this.calculation?.extendedSimulation,
            baseLine: newBaseLine,
          },
        }
      : {
          ...this.calculation,
          baseLine: newBaseLine,
        };
    return newCalculation;
  }
}
